import React from 'react';
import { TFunction } from 'react-i18next';
import {
  Text,
  Link,
  CodeSnippet,
  Stack,
  Trans,
  OrderedList,
} from '@piwikpro/ui-components';
import { App, ConsentManagementTool } from '../../../model';

const PRODUCT_ANALYTICS_INSTAL_METHOD_ACTION_TYPES = {
  GOOGLE_TAG_MANAGER: '@platform/command/appInstallation/googleTagManager',
};

interface GoogleTagManagerInstallProps {
  app: App
  t: TFunction
  installationCodeFailed: boolean
  snippet: string
  cmp: ConsentManagementTool | null
  handleCopyToClipboard: (result: boolean, productAnalyticsActionType: string) => void
}

const GoogleTagManagerInstall: React.FC<GoogleTagManagerInstallProps> = ({
  app,
  t,
  installationCodeFailed,
  snippet,
  cmp,
  handleCopyToClipboard,
}) => (
  <>
    <OrderedList>
      <Stack vertical>
        <Stack.Item fill>
          <Stack vertical spacing="narrow">
            <Stack.Item>
              <OrderedList.Item>
                <Trans
                  i18nKey="apps.details.installation.gtm.instruction.line1"
                  components={[
                    <Link href="https://tagmanager.google.com/#/home">Google Tag manager</Link>,
                  ]}
                />
              </OrderedList.Item>
            </Stack.Item>
            <Stack.Item>
              <OrderedList.Item>
                <Trans
                  i18nKey="apps.details.installation.gtm.instruction.line2"
                  components={[
                    <b>text</b>,
                  ]}
                />
              </OrderedList.Item>
            </Stack.Item>
            <Stack.Item>
              <OrderedList.Item>
                <Trans
                  i18nKey="apps.details.installation.gtm.instruction.line3"
                  components={[
                    <b>text</b>,
                  ]}
                />
              </OrderedList.Item>
            </Stack.Item>
            <Stack.Item>
              <OrderedList.Item>
                {t('apps.details.installation.gtm.instruction.line4')}
              </OrderedList.Item>
            </Stack.Item>
            <Stack.Item>
              <OrderedList.Item>
                <Trans
                  i18nKey="apps.details.installation.gtm.instruction.line5"
                  components={[
                    <b>text</b>,
                  ]}
                />
              </OrderedList.Item>
            </Stack.Item>
            <Stack.Item>
              <OrderedList.Item>
                <Trans
                  i18nKey="apps.details.installation.gtm.instruction.line6"
                  components={[
                    <b>text</b>,
                  ]}
                />
              </OrderedList.Item>
            </Stack.Item>
            <Stack.Item>
              <OrderedList.Item>
                {t('apps.details.installation.gtm.instruction.line7')}
              </OrderedList.Item>
            </Stack.Item>
            <Stack.Item fill>
              <CodeSnippet
                error={installationCodeFailed}
                code={snippet && !installationCodeFailed ? snippet : undefined}
                onCopy={result => handleCopyToClipboard(
                  result,
                  PRODUCT_ANALYTICS_INSTAL_METHOD_ACTION_TYPES.GOOGLE_TAG_MANAGER,
                )}
                mode="no-highlight"
              />
            </Stack.Item>
            <Stack.Item>
              <OrderedList.Item>
                <Trans
                  i18nKey="apps.details.installation.gtm.instruction.line8"
                  components={[
                    <b>text</b>,
                  ]}
                />
              </OrderedList.Item>
            </Stack.Item>
            <Stack.Item>
              <OrderedList.Item>
                <Trans
                  i18nKey="apps.details.installation.gtm.instruction.line9"
                  components={[
                    <b>text</b>,
                  ]}
                />
              </OrderedList.Item>
            </Stack.Item>
            {cmp === 'CookieInformationCMP' && (
              <Stack.Item>
                <OrderedList.Item>
                  <Trans
                    t={t}
                    i18nKey="apps.details.installation.gtm.instruction.line10-ci"
                    components={[
                      <Link href="https://support.cookieinformation.com/en/articles/5451615-integrate-cookie-information-consent-management-platform-with-google-tag-manager#h_d3c4be096d">
                        this step-by-step guide
                      </Link>,
                    ]}
                  />
                </OrderedList.Item>
              </Stack.Item>
            )}
            {cmp === 'OtherConsentTool' && (
              <Stack.Item>
                <OrderedList.Item>
                  <Trans
                    t={t}
                    i18nKey="apps.details.installation.gtm.instruction.line10-other"
                    components={[
                      <Link href="https://help.piwik.pro/support/privacy/how-can-i-integrate-a-third-party-cookie-consent-tool-with-piwik-pro/">
                        installation guide
                      </Link>,
                    ]}
                  />
                </OrderedList.Item>
              </Stack.Item>
            )}
            {cmp === 'PiwikLegacy' && (
              <Stack.Item>
                <OrderedList.Item>
                  <Trans
                    i18nKey="apps.details.installation.gtm.instruction.line11"
                    components={[
                      <Link href="https://help.piwik.pro/support/privacy/update-link-privacy-policy-page/">Add your privacy policy address</Link>,
                      <Link href="https://help.piwik.pro/support/privacy/setting-consent-manager/">turn off the consent form</Link>,
                    ]}
                  />
                </OrderedList.Item>
              </Stack.Item>
            )}
            <Stack.Item>
              <OrderedList.Item>
                <Trans
                  i18nKey="apps.details.installation.gtm.instruction.line10"
                  components={[
                    <Link href={`/analytics/#/${app.id}/settings/debugger/`}>tracker debugger</Link>,
                  ]}
                />
              </OrderedList.Item>
            </Stack.Item>
          </Stack>
        </Stack.Item>
      </Stack>
    </OrderedList>
    <Stack vertical>
      <Stack.Item previousSpacing="wide" />
      <Stack.Item>
        <Text as="p">
          {t('apps.details.installation.instruction.help.part1')}
          <Link href="https://help.piwik.pro/support/getting-started/google-tag-manager-install-a-tracking-code/">
            {t('apps.details.installation.links.installGuide')}
          </Link>
          {t('apps.details.installation.instruction.help.part2')}
          <Link href="http://community.piwik.pro/">
            {t('apps.details.installation.links.community')}
          </Link>
          .
        </Text>
      </Stack.Item>
    </Stack>
  </>
);

export default GoogleTagManagerInstall;
